import { useRef, useState } from 'react';

import posthog from 'posthog-js';
import { useAuth as useOidcAuth } from 'react-oidc-context';

import { useModal } from '@/components/providers/ModalProvider/ModalProvider';
import { useOffice } from '@/components/providers/OfficeProvider';
import ManualLoginModal from '@/features/login/ManualLoginModal';
import loginWithPolling from '@/lib/auth/pollingLogin';
import {
  type DialogCallbackArg,
  dialogErrorHandler,
  logDialogError,
} from '@/lib/office/dialogErrors';
import paths from '@/lib/paths';
import useBrokerIntegrations from '@/queries/hooks/useBrokerIntegrations';
import useCurrentBroker from '@/queries/hooks/useCurrentBroker';
import useDistribution from '@/queries/hooks/useDistribution';
import useMe from '@/queries/hooks/useMe';
import useLocalSettings from '@/stores/useLocalSettings';

import useBrokerTheme from './useBrokerTheme';

const hasEdgeUserAgent = () => navigator.userAgent.includes('Edg/');

export default function useAuth() {
  const { isOfficeEnvironment } = useOffice();
  const {
    signinRedirect,
    isAuthenticated,
    isLoading: isAuthLoading,
    removeUser,
    revokeTokens,
  } = useOidcAuth();
  const didReceiveMessage = useRef(false);
  const [isPollingLogin, setIsPollingLogin] = useState(false);
  const { showModal } = useModal();

  const overwriteTheme = useLocalSettings((state) => state.overwriteTheme);

  const { data: me, isLoading: isUserLoading } = useMe();

  const { data: broker, isLoading: isBrokerLoading } = useCurrentBroker();
  const { data: integrations } = useBrokerIntegrations(broker?.id ?? '', {
    enabled: Boolean(broker?.id),
  });

  const { data: distribution } = useDistribution(
    broker?.distribution_id ?? '',
    {
      enabled: !!broker?.distribution_id,
    },
  );

  useBrokerTheme(overwriteTheme ?? distribution?.theme.name);

  const dialogEventHandler =
    (dialog: Office.Dialog) => (args: DialogCallbackArg) => {
      if (didReceiveMessage.current) return;

      didReceiveMessage.current = true;

      dialog.close();

      if ('error' in args) {
        console.error(args.error);

        return;
      }

      const message = JSON.parse(args.message);

      const { code, scope, state, localStorageItem } = message;

      localStorage.setItem(localStorageItem[0], localStorageItem[1]);

      const url = new URL(window.location.href);
      url.pathname = paths.login.index;
      url.search = new URLSearchParams({ code, scope, state }).toString();

      window.location.href = url.toString();
    };

  const startPollingLogin = async () => {
    setIsPollingLogin(true);
    posthog.capture('Polling login started');

    try {
      const result = await loginWithPolling((authorizationUrl, cancelLogin) => {
        posthog.capture('Polling login browser window unsupported');
        showModal(
          ManualLoginModal,
          { loginUrl: authorizationUrl },
          { onDismiss: cancelLogin },
        );
      });

      if (result === 'cancelled') {
        posthog.capture('Polling login cancelled');
        setIsPollingLogin(false);
      }
    } catch (error) {
      console.error(error);
      posthog.capture('Polling login failed', {
        message: error instanceof Error ? error.message : String(error),
      });

      setIsPollingLogin(false);
    }
  };

  const login = () => {
    if (hasEdgeUserAgent()) {
      startPollingLogin();

      return;
    }

    if (!isOfficeEnvironment) {
      signinRedirect();

      return;
    }

    const dialogUrl = `${window.location.origin}/login/dialog`;
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { width: 60, height: 60 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          logDialogError(result.error.code);
          console.error(`Dialog failed to open:\n${result.error.message}`);

          return;
        }

        const dialog = result.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          dialogEventHandler(dialog),
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          dialogErrorHandler,
        );
      },
    );
  };

  const signout = async () => {
    await revokeTokens();
    await removeUser();

    try {
      posthog.reset();
    } catch {
      // no-op
    }
  };

  return {
    isAuthenticated,
    isLoading: isBrokerLoading || isAuthLoading || isUserLoading,
    isAuthLoading,
    isBrokerLoading,
    broker,
    me,
    distribution,
    integrations,
    login,
    isPollingLogin,
    signout,
  };
}
